
import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {Prop} from "vue-property-decorator";
import {EOrderStatuses, IOrder} from "@/model/Order/orderInterfaces";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {BoListPanel, IOnOffHandlers} from "bonomo/dist";

import LockerChip from "@/components/shared/chips/LockerChip.vue";
import LockerStatusChip from "@/components/shared/chips/LockerStatusChip.vue";
import OrderChip from "@/components/shared/chips/OrderChip.vue";
import UserChip from "@/components/shared/chips/UserChip.vue"
import Datim from "@/components/shared/Datim.vue";
import {IShortLocker} from '@/model/shortInterfaces';
import {OrderHelper} from "@/helpers/OrderHelper";
import UserSearch from "@/components/user/widget/UserSearch.vue";
import LockerSearch from "@/components/locker/widget/LockerSearch.vue";
import LockerNav from "@/components/locker/widget/LockerNav.vue";
import {IChipStyle} from "@/model/chipStyleInterfaces";

const lockerModule = namespace("lockerModule");
const orderModule = namespace("orderModule");

@Component({
  name: 'UserOrderList',
  components: {
    BoListPanel,
    Datim,
    LockerChip,
    LockerNav,
    LockerSearch,
    LockerStatusChip,
    OrderChip,
    UserChip,
    UserSearch,
  }
})
export default class PackagesToTransfer extends Vue {
  @Prop()
  protected lockerId!: number;

  @lockerModule.Getter('packagesToTransfer')
  private orders!: IOrder[];

  @lockerModule.Action('fetchPackagesToTransfer')
  private fetchPackagesToTransferAction!: (filters: any) => void;

  @lockerModule.Action('fetchLockers')
  private fetchLockers!: () => void;

  @lockerModule.Getter('lockers')
  private lockers!: IShortLocker[];

  @orderModule.Getter('order')
  private order!: IOrder;

  @orderModule.Action('collectByAdministrator')
  protected collectByAdministrator!: (param: {orderId: number, collect_reason: string, openCell: boolean, closeOrder: boolean}) => Promise<any>;

  @orderModule.Action('deliverByAdministrator')
  protected deliverByAdministrator!: (param: {orderId: number, reason: string, openCell: boolean}) => Promise<any>;

  protected loading: boolean = false;
  protected ordersProcessed: number[] = [];

  private subscribes: IOnOffHandlers = {};

  protected get extraFilters(): object {
    return {
      lockerId: [this.lockerId]
    }
  }

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id'), align: 'start', sortable: true, value: 'id' },
      { text: this.$t('common.type'), align: 'start',sortable: true, value: 'type'},
      { text: this.$t('common.name'), align: 'start', sortable: true, value: 'user' },
      { text: this.$t('lockers.locker.title'), align: 'start', sortable: true, value: 'locker_id' },
      { text: this.$t('lockers.locker.title'), align: 'start', sortable: true, value: 'destination_locker_id' },
      { text: this.$t('common.status'), align: 'start', sortable: true, value: 'status' },
      { text: this.$t('orders.order.locker_code'), align: 'center', sortable: true, value: 'locker_code' },
      { text: this.$t("common.createdAt"), align: 'start', sortable: true, value: 'created_at' },
      // { text: this.$t('orders.order.due'), align: 'start', sortable: true, value: 'due' },
      { text: "", align: 'center', sortable: false, value: 'action' },
    ];
  }

  created() {
    // it would be nice to clean orderModule.order state but it is a rare case that it would matter
    this.subscribes = this.$ons({
      'backend.call.OK.orderCollectedByAdministrator': this.onLoaded,
      'backend.call.KO.orderCollectedByAdministrator': this.onKO,
      'backend.call.OK.orderDeliveredByAdministrator': this.onLoaded,
      'backend.call.KO.orderDeliveredByAdministrator': this.onKO,
    })
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  protected getLockerName(lockerId: number): string {
    for (let i = 0; i < this.lockers.length; i ++) {
      if(this.lockers[i].id == lockerId) {
        return this.lockers[i].name;
      }
    }
    return '---';
  }

  protected loadData(initial?: boolean){
    this.fetchPackagesToTransfer();
    if (initial) {
      this.fetchLockers();
    }
  }

  protected fetchPackagesToTransfer() {
    if (!this.lockerId) {
      return;
    }
    this.fetchPackagesToTransferAction(this.lockerId);
  }

  protected findLocker(id: number) {
    return this.lockers.find((locker :IShortLocker) => locker.id == id) || {};
  }

  protected linkToOrder(id: number) {
    return {name:'order',params: {orderId:id}};
  }

  protected orderIsProcessed(orderId: number) {
    return this.ordersProcessed.includes(orderId);
  }

  protected orderType(type: string): IChipStyle {
    return OrderChip.chipStyleOf(type);
  }

  protected onLoaded() {
    this.loading = false;
    // this will nicely remove the last processed order from the list, hopefuilly.
    if (!this.ordersProcessed.includes(this.order.id)) {
      this.ordersProcessed.push(this.order.id);
    }
  }

  protected onKO() {
    this.loading = false;
  }

  protected onOpenBtnClicked(order: IOrder): void {
    if (order.status == EOrderStatuses.status_order_awaiting_collect) {
      this.loading = true;
      const isHomeDelivery = !order.transfer_destination_locker_id;
      this.collectByAdministrator({
        orderId: order.id,
        collect_reason: '' + this.$t('orders.order.types.package_transfer'),
        openCell: true,
        closeOrder: isHomeDelivery,
      });
    }
    else if (order.status == EOrderStatuses.status_order_sp_collected ||
      order.status == EOrderStatuses.status_order_in_route_delivery
    ) {
      this.loading = true;
      this.deliverByAdministrator({
        orderId: order.id,
        reason: '' + this.$t('orders.order.types.package_transfer'),
        openCell: true,
      });
    }
  }

  private typeChipTitle(order: IOrder): string {
    return order.type
        ? '' + (this.$t("orders.order.types." + (order.type || "office_order")) || this.$t("orders.order.types.unknown"))
        : '';
  }

  protected spikeErrorTitle(order: IOrder): string {
    const err = order.spike_error;
    if (!err) return '';
    return this.$t('orders.order.logs.actions.700') + ': ' + err.operation + ' - ' + err.message;
  }

  private onLockerSelected(newLockerId: number) {
    this.$router.push({name: 'packagesToTransfer', params: {lockerId: '' + newLockerId}});
  }

}
